"use client";

import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useSession } from "next-auth/react";
import { getPusherClient } from "@/lib/pusher-client";
import { getUnreadCount } from "@/app/actions/messaging";

interface Conversation {
  id: string;
  lastMessageAt: Date | null;
  lastMessagePreview: string | null;
  otherUser: {
    id: string;
    name: string | null;
    email: string | null;
    image: string | null;
  };
  listing: {
    id: string;
    title: string;
    address: string;
    price: number;
    images: string[];
  } | null;
  unreadCount: number;
}

interface MessagingContextType {
  // Widget state
  isWidgetOpen: boolean;
  isWidgetMinimized: boolean;
  openWidget: (conversationId?: string) => void;
  closeWidget: () => void;
  toggleWidgetMinimize: () => void;

  // Modal state
  isModalOpen: boolean;
  openModal: (conversationId?: string, listingId?: string) => void;
  closeModal: () => void;

  // Conversation state
  activeConversationId: string | null;
  activeListingId: string | null;
  setActiveConversation: (conversationId: string | null) => void;
  conversations: Conversation[];
  setConversations: (conversations: Conversation[]) => void;

  // Unread count
  totalUnreadCount: number;
  fetchUnreadCount: () => Promise<void>;

  // Combined actions
  openMessaging: (mode?: "widget" | "modal", conversationId?: string) => void;
  closeAll: () => void;
}

const MessagingContext = createContext<MessagingContextType | undefined>(undefined);

export function useMessaging() {
  const context = useContext(MessagingContext);
  if (!context) {
    throw new Error("useMessaging must be used within MessagingProvider");
  }
  return context;
}

interface MessagingProviderProps {
  children: React.ReactNode;
}

export function MessagingProvider({ children }: MessagingProviderProps) {
  const { data: session, status } = useSession();

  // Widget state
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const [isWidgetMinimized, setIsWidgetMinimized] = useState(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Conversation state
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [activeListingId, setActiveListingId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);

  // Unread count
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (status === "authenticated" && session?.user?.id) {
      try {
        const count = await getUnreadCount();
        setTotalUnreadCount(count);
      } catch (error) {
        console.error("Failed to fetch unread count:", error);
      }
    }
  }, [session, status]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (status === "authenticated" && session?.user?.id) {
      fetchUnreadCount();

      const pusher = getPusherClient();
      const channel = pusher.subscribe(`user-${session.user.id}`);

      channel.bind("unread-count", (data: { unreadCount: number }) => {
        setTotalUnreadCount(data.unreadCount);
      });

      // Listen for new messages to update widget/modal in real-time
      channel.bind("new-message", () => {
        // Trigger refresh of conversations list
        // This will be handled by the components listening to this event
      });

      return () => {
        channel.unbind_all();
        channel.unsubscribe();
      };
    }
  }, [session, status, fetchUnreadCount]);

  // Widget actions
  const openWidget = useCallback((conversationId?: string) => {
    setIsWidgetOpen(true);
    setIsWidgetMinimized(false);
    if (conversationId) {
      setActiveConversationId(conversationId);
    }
  }, []);

  const closeWidget = useCallback(() => {
    setIsWidgetOpen(false);
    setIsWidgetMinimized(false);
  }, []);

  const toggleWidgetMinimize = useCallback(() => {
    setIsWidgetMinimized(!isWidgetMinimized);
  }, [isWidgetMinimized]);

  // Modal actions
  const openModal = useCallback((conversationId?: string, listingId?: string) => {
    setIsModalOpen(true);
    setIsWidgetOpen(false); // Close widget when opening modal
    if (conversationId) {
      setActiveConversationId(conversationId);
    }
    if (listingId) {
      setActiveListingId(listingId);
    }
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Combined actions
  const openMessaging = useCallback((mode: "widget" | "modal" = "widget", conversationId?: string) => {
    if (mode === "widget") {
      openWidget(conversationId);
    } else {
      openModal(conversationId);
    }
  }, [openWidget, openModal]);

  const closeAll = useCallback(() => {
    setIsWidgetOpen(false);
    setIsModalOpen(false);
    setIsWidgetMinimized(false);
  }, []);

  const setActiveConversation = useCallback((conversationId: string | null) => {
    setActiveConversationId(conversationId);
  }, []);

  const value: MessagingContextType = {
    // Widget state
    isWidgetOpen,
    isWidgetMinimized,
    openWidget,
    closeWidget,
    toggleWidgetMinimize,

    // Modal state
    isModalOpen,
    openModal,
    closeModal,

    // Conversation state
    activeConversationId,
    activeListingId,
    setActiveConversation,
    conversations,
    setConversations,

    // Unread count
    totalUnreadCount,
    fetchUnreadCount,

    // Combined actions
    openMessaging,
    closeAll,
  };

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
}